import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // 1. Extract data from the fetched product HTML
    const title = this.dataset.productTitle || productGrid.querySelector('.view-product-title a')?.textContent || 'Dessert';
    const priceEl = productGrid.querySelector('product-price');
    const mediaImg = productGrid.querySelector('.product-information__media img');
    const imageSrc = mediaImg ? mediaImg.src : '';

    // Locate description block inside details
    const descEl = productGrid.querySelector('rte-formatter') || productGrid.querySelector('.rte') || productGrid.querySelector('.product-description') || productGrid.querySelector('.product-details p');
    const descriptionHTML = descEl ? descEl.innerHTML : 'Handcrafted premium dessert baked fresh daily.';

    const variantPicker = productGrid.querySelector('variant-picker');
    const productForm = productGrid.querySelector('product-form-component') || productGrid.querySelector('.buy-buttons-block');

    // 2. Re-build the productGrid container to match the custom premium design
    productGrid.innerHTML = '';
    productGrid.className = 'product-information__grid cr-qadd-layout-stage';

    const card = document.createElement('div');
    card.className = 'cr-qadd-card';

    // Dome image header
    const header = document.createElement('div');
    header.className = 'cr-qadd-header';
    const dome = document.createElement('div');
    dome.className = 'cr-qadd-image-dome';
    if (imageSrc) {
      const img = document.createElement('img');
      img.src = imageSrc;
      img.alt = title;
      img.className = 'cr-qadd-img';
      dome.appendChild(img);
    }
    header.appendChild(dome);
    card.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'cr-qadd-body';

    // Meta (Title, Subtitle & Price)
    const meta = document.createElement('div');
    meta.className = 'cr-qadd-meta';

    const metaLeft = document.createElement('div');
    metaLeft.className = 'cr-qadd-meta-left';
    const titleEl = document.createElement('h2');
    titleEl.className = 'cr-qadd-title';
    titleEl.textContent = title;
    const subtitleEl = document.createElement('span');
    subtitleEl.className = 'cr-qadd-subtitle';
    subtitleEl.textContent = 'Worth Giving In To';
    metaLeft.appendChild(titleEl);
    metaLeft.appendChild(subtitleEl);
    meta.appendChild(metaLeft);

    if (priceEl) {
      const priceWrap = document.createElement('div');
      priceWrap.className = 'cr-qadd-price-wrap';
      priceWrap.appendChild(priceEl.cloneNode(true));
      meta.appendChild(priceWrap);
    }
    body.appendChild(meta);

    // Details / Reviews Tabs
    const tabs = document.createElement('div');
    tabs.className = 'cr-qadd-tabs';

    const detailsTab = document.createElement('button');
    detailsTab.type = 'button';
    detailsTab.className = 'cr-qadd-tab cr-qadd-tab--active';
    detailsTab.textContent = 'Details';
    detailsTab.setAttribute('data-target', 'cr-qadd-desc-box');

    const reviewsTab = document.createElement('button');
    reviewsTab.type = 'button';
    reviewsTab.className = 'cr-qadd-tab';
    reviewsTab.textContent = 'Reviews';
    reviewsTab.setAttribute('data-target', 'cr-qadd-reviews-box');

    tabs.appendChild(detailsTab);
    tabs.appendChild(reviewsTab);
    body.appendChild(tabs);

    // Description Pane
    const descBox = document.createElement('div');
    descBox.id = 'cr-qadd-desc-box';
    descBox.className = 'cr-qadd-tab-pane';
    const descText = document.createElement('div');
    descText.className = 'cr-qadd-description-text';
    descText.innerHTML = descriptionHTML;
    descBox.appendChild(descText);
    body.appendChild(descBox);

    // Reviews Pane
    const reviewsBox = document.createElement('div');
    reviewsBox.id = 'cr-qadd-reviews-box';
    reviewsBox.className = 'cr-qadd-tab-pane cr-qadd-hidden';
    reviewsBox.innerHTML = `
      <div class="cr-qadd-reviews-placeholder">
        <div class="cr-qadd-stars">★★★★★</div>
        <p class="cr-qadd-reviews-quote">"So soft and delicious! Absolutely the best."</p>
        <span class="cr-qadd-reviews-author">— Sweet Tooth</span>
      </div>
    `;
    body.appendChild(reviewsBox);

    // Variant Picker
    if (variantPicker) {
      const pickerWrap = document.createElement('div');
      pickerWrap.className = 'cr-qadd-variants-wrap';
      pickerWrap.appendChild(variantPicker);
      body.appendChild(pickerWrap);
    }

    // Buy Form Component
    if (productForm) {
      const formWrap = document.createElement('div');
      formWrap.className = 'cr-qadd-form-wrap';
      formWrap.appendChild(productForm);
      body.appendChild(formWrap);
    }

    card.appendChild(body);
    productGrid.appendChild(card);

    // Sync view-event-payload and morph into modal content
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // Re-bind tab listeners on the morphed elements
    const activeModal = document.getElementById('quick-add-modal-content') || modalContent;
    if (activeModal) {
      const mDetailsTab = activeModal.querySelector('[data-target="cr-qadd-desc-box"]');
      const mReviewsTab = activeModal.querySelector('[data-target="cr-qadd-reviews-box"]');
      const mBody = activeModal.querySelector('.cr-qadd-body');

      if (mDetailsTab && mReviewsTab && mBody) {
        const bindTabClick = (btn) => {
          btn.addEventListener('click', (e) => {
            const targetId = e.currentTarget.getAttribute('data-target');
            mBody.querySelectorAll('.cr-qadd-tab').forEach(t => t.classList.remove('cr-qadd-tab--active'));
            mBody.querySelectorAll('.cr-qadd-tab-pane').forEach(p => p.classList.add('cr-qadd-hidden'));

            e.currentTarget.classList.add('cr-qadd-tab--active');
            const targetPane = mBody.querySelector(`#${targetId}`);
            if (targetPane) targetPane.classList.remove('cr-qadd-hidden');
          });
        };
        bindTabClick(mDetailsTab);
        bindTabClick(mReviewsTab);
      }
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
